import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getClinicianCommission,
  getClinicianProfileById,
  updateClinicianCommission,
} from "../../../../api/services/api";
import Loader from "../../../../components/Loader";
import { RoleGuard } from "../../../../components/RBAC";

function TherapistCommission() {
  const { id } = useParams();
  const clinicianId = Number(id);
  const queryClient = useQueryClient();
  const [commission, setCommission] = useState<number>(70);

  const { data: profileData, isLoading: profileLoading } = useQuery(
    ["clinician-profile", id],
    getClinicianProfileById,
    { enabled: Boolean(id) }
  );

  const { data: commissionData, isLoading: commissionLoading } = useQuery(
    ["clinician-commission", id],
    getClinicianCommission,
    { enabled: Boolean(id) }
  );

  useEffect(() => {
    const commissionPayload = commissionData as any;
    const profilePayload = profileData as any;
    const value =
      commissionPayload?.data?.commission ??
      profilePayload?.data?.commision ??
      profilePayload?.data?.commission;
    if (typeof value === "number" && !Number.isNaN(value)) {
      setCommission(value);
    }
  }, [commissionData, profileData]);

  const { mutate: saveCommission, isLoading: isSaving } = useMutation(
    (value: number) => updateClinicianCommission(clinicianId, value),
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries(["clinician-commission", id]);
        queryClient.invalidateQueries(["clinician-profile", id]);
        queryClient.invalidateQueries("therapists");
        toast.success(
          res?.message || res?.data?.message || "Commission updated successfully"
        );
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message || "Failed to update commission"
        );
      },
    }
  );

  const commissionPayload = commissionData as any;
  const profilePayload = profileData as any;
  const clinicianName =
    commissionPayload?.data?.clinicianName ||
    profilePayload?.data?.users?.fullName ||
    profilePayload?.data?.users?.full_name ||
    "Clinician";

  const platformShare = Math.max(0, Math.min(100, 100 - (commission || 0)));
  const sampleAmount = 100;
  const clinicianSample = (sampleAmount * (commission || 0)) / 100;
  const platformSample = sampleAmount - clinicianSample;

  const handleSave = () => {
    if (Number.isNaN(commission) || commission < 0 || commission > 100) {
      toast.error("Commission must be between 0 and 100");
      return;
    }
    saveCommission(commission);
  };

  if (profileLoading || commissionLoading) {
    return <Loader />;
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" mb={0.5}>
          Commission
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set the clinician share for {clinicianName}. Platform keeps the rest.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: "1px solid #eee",
          maxWidth: 520,
          borderRadius: 2,
        }}
      >
        <TextField
          fullWidth
          type="number"
          label="Clinician commission"
          value={commission}
          onChange={(e) => setCommission(parseFloat(e.target.value) || 0)}
          inputProps={{ min: 0, max: 100, step: 0.1 }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          helperText="Percentage the clinician receives from each paid session (0–100)"
          sx={{ mb: 3 }}
        />

        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr"
          gap={2}
          mb={3}
          sx={{
            p: 2,
            bgcolor: "#F8F9FA",
            borderRadius: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Clinician share
            </Typography>
            <Typography variant="h6">{commission || 0}%</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Platform share
            </Typography>
            <Typography variant="h6">{platformShare}%</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              On {sampleAmount} SAR session
            </Typography>
            <Typography variant="body1">
              Clinician: {clinicianSample.toFixed(2)} SAR
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              On {sampleAmount} SAR session
            </Typography>
            <Typography variant="body1">
              Platform: {platformSample.toFixed(2)} SAR
            </Typography>
          </Box>
        </Box>

        <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            fullWidth
          >
            {isSaving ? <CircularProgress size={22} /> : "Save Commission"}
          </Button>
        </RoleGuard>
      </Paper>
    </Box>
  );
}

export default TherapistCommission;
